import { useCallback, useEffect, useMemo, useRef } from "react";
import { useCreateRowSelectionStore } from "@/components/data-table/hooks/useCreateRowSelectionStore";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";

interface UseStoreGroupSelectionOptions {
  stores: ReadStoreGroupMember[];
  storeGroupId: string | undefined;
  isLoaded: boolean;
}

/**
 * Hook to manage store selection state for the store group editor using RowSelectionStore.
 *
 * This hook:
 * 1. Creates a RowSelectionStore for managing current selection
 * 2. Tracks the baseline (stores that belong to this group)
 * 3. Syncs selection with loaded stores data
 * 4. Provides methods to calculate add/delete arrays for the API
 */
export const useStoreGroupSelection = ({
  stores,
  storeGroupId,
  isLoaded,
}: UseStoreGroupSelectionOptions) => {
  const store = useCreateRowSelectionStore();

  /**
   * Tracks baseline state for all stores we've seen.
   * Maps storeId -> shouldBeSelected (based on store_group_id === storeGroupId).
   */
  const baselineRef = useRef<Map<string, boolean>>(new Map());

  /**
   * Tracks if we've done the initial sync for this storeGroupId.
   * Prevents re-syncing on every stores array change.
   */
  const initialSyncDoneRef = useRef<Set<string>>(new Set());

  // Sync selection when stores load
  useEffect(() => {
    if (!isLoaded || !storeGroupId) return;

    const storeIdsToSelect: string[] = [];

    stores.forEach((s) => {
      const shouldBeSelected = s.store_group_id === storeGroupId;
      const storeId = s.id;

      // Update baseline for this store
      baselineRef.current.set(storeId, shouldBeSelected);

      // Only auto-select on initial load for each store
      if (!initialSyncDoneRef.current.has(storeId) && shouldBeSelected) {
        storeIdsToSelect.push(storeId);
      }

      initialSyncDoneRef.current.add(storeId);
    });

    // Select stores that belong to this group
    if (storeIdsToSelect.length > 0) {
      storeIdsToSelect.forEach((id) => {
        if (!store.isSelected(id)) {
          store.toggle(id);
        }
      });
    }
  }, [isLoaded, storeGroupId, stores, store]);

  // Reset when storeGroupId changes
  useEffect(() => {
    store.clear();
    baselineRef.current.clear();
    initialSyncDoneRef.current.clear();
  }, [storeGroupId, store]);

  /**
   * Calculate stores to add (selected but weren't in baseline)
   */
  const getStoresToAdd = useMemo(
    () => () => {
      const currentSelection = store.getSelection();
      const toAdd: string[] = [];

      currentSelection.forEach((id) => {
        const wasInBaseline = baselineRef.current.get(id);
        if (!wasInBaseline) {
          toAdd.push(id);
        }
      });

      return toAdd;
    },
    [store],
  );

  /**
   * Calculate stores to delete (were in baseline but no longer selected)
   */
  const getStoresToDelete = useMemo(
    () => () => {
      const currentSelection = store.getSelection();
      const toDelete: string[] = [];

      baselineRef.current.forEach((wasSelected, id) => {
        if (wasSelected && !currentSelection.has(id)) {
          toDelete.push(id);
        }
      });

      return toDelete;
    },
    [store],
  );

  /**
   * Reset tracking after successful save
   */
  const onSaveSuccess = useMemo(
    () => () => {
      // Update baseline to match current selection
      const currentSelection = store.getSelection();
      baselineRef.current.clear();
      currentSelection.forEach((id) => {
        baselineRef.current.set(id, true);
      });
      initialSyncDoneRef.current.clear();
      currentSelection.forEach((id) => {
        initialSyncDoneRef.current.add(id);
      });
    },
    [store],
  );

  /**
   * Check if there are any pending selection changes.
   * Designed to be used with useSyncExternalStore for reactive updates.
   */
  const getHasSelectionChanges = useCallback(() => {
    const currentSelection = store.getSelection();

    // Check for additions (selected but not in baseline)
    for (const id of currentSelection) {
      if (!baselineRef.current.get(id)) {
        return true;
      }
    }

    // Check for deletions (in baseline but not selected)
    for (const [id, wasSelected] of baselineRef.current) {
      if (wasSelected && !currentSelection.has(id)) {
        return true;
      }
    }

    return false;
  }, [store]);

  return {
    store,
    getStoresToAdd,
    getStoresToDelete,
    onSaveSuccess,
    getHasSelectionChanges,
  };
};
